import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import SortableTh from '../components/SortableTh';
import { useSortableTable } from '../utils/sortableTable';
import { REFERRAL_SORT_COLUMNS } from '../constants/listTableSort';
import { useAuth } from '../hooks/useAuth';
import TableExportButton from '../components/TableExportButton';

const REFERRAL_EXPORT_COLUMNS = [
  { key: 'id', label: 'Referral ID' },
  { key: 'referredName', label: 'Referred Candidate' },
  { key: 'referredEmail', label: 'Email' },
  { key: 'jobTitle', label: 'Job' },
  { key: 'clientName', label: 'Client' },
  { key: 'referrerName', label: 'Referrer' },
  { key: 'createdAt', label: 'Date', format: r => (r.createdAt ? new Date(r.createdAt).toLocaleString() : '—') }
];

export default function Referrals() {
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['referrals'],
    queryFn: () => api.get('/referrals').then(r => r.data),
    enabled: user?.role === 'admin' || user?.role === 'recruiter'
  });

  const { sort, toggleSort, sortedRows: sortedReferrals } = useSortableTable(
    data || [],
    REFERRAL_SORT_COLUMNS
  );

  if (!(user?.role === 'admin' || user?.role === 'recruiter')) {
    return <div className="p-6 text-sm text-[hsl(var(--ds-muted-fg))]">Referrals are available for admin/recruiter accounts.</div>;
  }

  if (isLoading) return <div className="p-6">Loading referrals…</div>;
  if (isError) return <div className="p-6 text-red-600">{error?.response?.data?.message || error?.message || 'Failed to load referrals'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
        <h1 className="text-xl font-semibold">Candidate Referrals</h1>
        <p className="text-sm text-[hsl(var(--ds-muted-fg))] mt-1">Referrals submitted by candidates from Current Openings.</p>
        </div>
        <TableExportButton
          filename="candidate_referrals"
          sheetName="Candidate Referrals"
          columns={REFERRAL_EXPORT_COLUMNS}
          rows={sortedReferrals}
        />
      </div>
      <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-2">Sl No</th>
                <SortableTh label="Referral ID" sortKey="id" sort={sort} onSort={toggleSort} className="px-4 py-2" />
                <SortableTh label="Referred Candidate" sortKey="referred" sort={sort} onSort={toggleSort} className="px-4 py-2" />
                <SortableTh label="Job" sortKey="job" sort={sort} onSort={toggleSort} className="px-4 py-2" />
                <SortableTh label="Client" sortKey="client" sort={sort} onSort={toggleSort} className="px-4 py-2" />
                <SortableTh label="Referrer" sortKey="referrer" sort={sort} onSort={toggleSort} className="px-4 py-2" />
                <SortableTh label="Date" sortKey="date" sort={sort} onSort={toggleSort} className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedReferrals.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">No referrals yet.</td>
                </tr>
              ) : (
                sortedReferrals.map((r, idx) => (
                  <tr key={r.id} className="border-b border-[hsl(var(--ds-border))]">
                    <td className="px-4 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                    <td className="px-4 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">{r.id}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.referredName}</div>
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))]">{r.referredEmail}</div>
                    </td>
                    <td className="px-4 py-2">{r.jobTitle || r.jobId}</td>
                    <td className="px-4 py-2">{r.clientName || r.clientId}</td>
                    <td className="px-4 py-2">{r.referrerName || r.referrerCandidateId}</td>
                    <td className="px-4 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">{new Date(r.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
